import { useState } from 'react';
import { Link } from 'react-router-dom';
import { articles, categories, faqItems, features, stats, testimonials } from '@/mocks/moguvideo';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '蘑菇视频',
    url: 'https://lygshjx.com.cn/',
    description: '蘑菇视频官网是您访问蘑菇视频的官方正版入口，提供海量高清视频内容、安全下载、会员服务及最新版本更新。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://lygshjx.com.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '蘑菇视频',
    url: 'https://lygshjx.com.cn/',
    logo: 'https://lygshjx.com.cn/logo.png',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-0000',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
    sameAs: [
      'https://weibo.com/moguvideo',
      'https://www.zhihu.com/org/moguvideo',
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
        data-schema="website"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
        data-schema="organization"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        data-schema="faq"
      />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20modern%20minimalist%20living%20room%20with%20a%20large%20flatscreen%20TV%20displaying%20vibrant%20nature%20documentary%20content%2C%20warm%20coral%20and%20amber%20ambient%20lighting%2C%20cozy%20atmosphere%2C%20evening%20mood%2C%20cinematic%20photography%2C%20clean%20composition%2C%20professional%20interior%20design%2C%20soft%20shadows&width=1800&height=900&seq=moguvideo-hero-bg&orientation=landscape"
            alt="蘑菇视频 - 官方正版视频平台"
            title="蘑菇视频官网 - 官方正版视频平台入口"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-5 leading-tight">
              蘑菇视频官网
            </h1>
            <p className="text-base md:text-xl text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              官方正版视频平台入口，海量高清内容，安全下载，畅享极致观影体验
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/dianying"
                className="px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-base font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                立即观看
              </Link>
              <Link
                to="/article/moguvideo-membership-guide"
                className="px-8 py-3.5 bg-white/20 text-white rounded-full text-base font-semibold hover:bg-white/30 backdrop-blur-sm transition-all whitespace-nowrap cursor-pointer"
              >
                了解会员
              </Link>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                为什么选择蘑菇视频？
              </h2>
              <p className="text-foreground-500 max-w-xl mx-auto text-sm md:text-base">
                六大核心优势，打造极致的观影体验
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
              {features.map((feat, i) => (
                <div
                  key={i}
                  className="bg-background-50 rounded-xl p-6 md:p-8 hover:bg-background-50/80 transition-colors cursor-default"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg mb-4">
                    <i className={`${feat.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-base">
                    {feat.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {feat.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                海量内容分类
              </h2>
              <p className="text-foreground-500 max-w-xl mx-auto text-sm md:text-base">
                八大内容分类，满足您的所有观影需求
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="bg-background-100 rounded-xl p-5 md:p-6 text-center hover:bg-primary-100/60 transition-colors group cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg mx-auto mb-3 group-hover:bg-primary-200 transition-colors">
                    <i className={`${cat.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm md:text-base mb-1 whitespace-nowrap">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed hidden sm:block">
                    {cat.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Articles */}
        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                最新资讯
              </h2>
              <p className="text-foreground-500 max-w-xl mx-auto text-sm md:text-base">
                掌握蘑菇视频最新动态和观影攻略
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
              {articles.slice(0, 3).map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="bg-background-50 rounded-xl overflow-hidden hover:bg-background-50/80 transition-colors group cursor-pointer"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      title={`${article.title} - 蘑菇视频`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-foreground-400 mb-2">
                      <span>{article.publishDate}</span>
                      <span>·</span>
                      <span>{article.readTime}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/category/dianying"
                className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看更多 <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                用户好评
              </h2>
              <p className="text-foreground-500 max-w-xl mx-auto text-sm md:text-base">
                听听蘑菇视频的用户怎么说
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-100 rounded-xl p-6 md:p-7">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    "{t.content}"
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Download */}
        <section id="download" className="w-full bg-primary-500 py-14 md:py-20">
          <div className="w-full px-4 md:px-6 lg:px-10 text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
              立即下载蘑菇视频
            </h2>
            <p className="text-background-50/80 max-w-lg mx-auto mb-8 text-sm md:text-base">
              支持安卓和iOS平台，随时随地畅享高清视频
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#"
                className="inline-flex items-center gap-2.5 px-8 py-3.5 bg-background-50 text-primary-600 rounded-full text-base font-semibold hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-android-line text-xl"></i>
                安卓版下载
              </a>
              <a
                href="#"
                className="inline-flex items-center gap-2.5 px-8 py-3.5 bg-background-50/20 text-background-50 rounded-full text-base font-semibold hover:bg-background-50/30 backdrop-blur-sm transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-apple-line text-xl"></i>
                iOS版下载
              </a>
            </div>
            <p className="text-background-50/60 text-xs mt-5">蘑菇视频最新版本 V3.0 | 安全无毒 · 官方正版</p>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-foreground-500 max-w-xl mx-auto text-sm md:text-base">
                关于蘑菇视频的常见问题解答
              </p>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqItems.map((faq, i) => (
                <div
                  key={i}
                  className="bg-background-50 rounded-xl overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="font-medium text-foreground-900 text-sm md:text-base pr-4">
                      {faq.question}
                    </span>
                    <i
                      className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-lg text-foreground-500 shrink-0 transition-transform duration-200`}
                    ></i>
                  </button>
                  <div
                    className={`px-6 overflow-hidden transition-all duration-300 ${
                      openFaq === i ? 'pb-5 max-h-96' : 'max-h-0'
                    }`}
                  >
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}